/**
 * One browser capability abstraction for the worker web/PWA client.
 *
 * Everything here is FEATURE DETECTION. User-agent strings are consulted only
 * where the browser exposes no other way to give honest guidance (iOS requires
 * a Home Screen install before Web Push can be enabled at all, and that
 * requirement is not feature-detectable before installation).
 *
 * Capability is NOT authorization and NOT entitlement. It answers only "can
 * this browser physically do this?" — see `webPlatformFeatures.ts` for the
 * combined resolution, and the backend for the authoritative rules.
 */

export interface BrowserCapabilities {
  serviceWorker: boolean
  push: boolean
  notifications: boolean
  geolocation: boolean
  camera: boolean
  microphone: boolean
  indexedDb: boolean
  backgroundSync: boolean
  periodicBackgroundSync: boolean
  share: boolean
  clipboard: boolean
  vibration: boolean
  barcodeDetector: boolean
  standalone: boolean
  online: boolean
  /** Installed-app requirement for push on this platform (iOS/iPadOS Safari). */
  pushRequiresInstall: boolean
  /**
   * Browser cannot keep a suspended app running in the background. iOS
   * suspends the browser/Home Screen app aggressively, so no continuous
   * background breadcrumbing or background geofence execution is promised.
   */
  backgroundLocationUnavailable: boolean
}

/** The subset of browser globals capability detection reads. */
export interface CapabilityEnvironment {
  navigator?: {
    serviceWorker?: unknown
    geolocation?: unknown
    mediaDevices?: { getUserMedia?: unknown } | undefined
    share?: unknown
    clipboard?: unknown
    vibrate?: unknown
    onLine?: boolean
    userAgent?: string
    standalone?: boolean
    maxTouchPoints?: number
  }
  window?: {
    PushManager?: unknown
    Notification?: unknown
    indexedDB?: unknown
    BarcodeDetector?: unknown
    matchMedia?: (query: string) => { matches: boolean }
  }
  /**
   * `ServiceWorkerRegistration.prototype`. Background Sync is detected by
   * PRESENCE of the accessor, never by reading it: `sync` and `periodicSync`
   * are prototype getters and invoking one with the prototype as `this` throws
   * "Illegal invocation".
   */
  serviceWorkerRegistrationPrototype?: object
}

const has = (value: unknown) => value !== undefined && value !== null

/** Presence check that never invokes a prototype getter. */
const hasMember = (target: object | undefined, member: string) =>
  Boolean(target) && member in (target as object)

/**
 * iOS/iPadOS: every browser is WebKit, and Web Push is only available to a web
 * app added to the Home Screen. `PushManager` may be absent in the Safari tab
 * and present once installed, so detection alone cannot explain the reason —
 * this is the one place a UA hint is unavoidable.
 */
export function isIosWebkit(env: CapabilityEnvironment): boolean {
  const userAgent = env.navigator?.userAgent || ''
  if (/iPad|iPhone|iPod/i.test(userAgent)) return true
  // iPadOS 13+ reports a desktop-Mac UA; touch points disambiguate it.
  return /Macintosh/i.test(userAgent) && (env.navigator?.maxTouchPoints || 0) > 1
}

export function isStandaloneDisplay(env: CapabilityEnvironment): boolean {
  // iOS Safari exposes the legacy `navigator.standalone`; everyone else answers
  // the display-mode media query.
  if (env.navigator?.standalone === true) return true
  const windowRef = env.window
  if (typeof windowRef?.matchMedia !== 'function') return false
  for (const mode of ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay']) {
    try {
      // Called on `windowRef` on purpose: `matchMedia` is a native method and
      // throws "Illegal invocation" if detached from its receiver.
      if (windowRef.matchMedia(`(display-mode: ${mode})`).matches) return true
    } catch {
      // Older engines throw on unknown display modes; treat as not standalone.
    }
  }
  return false
}

export function resolveBrowserCapabilities(env: CapabilityEnvironment): BrowserCapabilities {
  const navigatorRef = env.navigator
  const windowRef = env.window
  const registrationPrototype = env.serviceWorkerRegistrationPrototype
  const serviceWorker = has(navigatorRef?.serviceWorker)
  const push = serviceWorker && has(windowRef?.PushManager)
  const standalone = isStandaloneDisplay(env)
  const ios = isIosWebkit(env)

  return {
    serviceWorker,
    push,
    notifications: has(windowRef?.Notification),
    geolocation: has(navigatorRef?.geolocation),
    camera: has(navigatorRef?.mediaDevices?.getUserMedia),
    microphone: has(navigatorRef?.mediaDevices?.getUserMedia),
    indexedDb: has(windowRef?.indexedDB),
    backgroundSync: serviceWorker && hasMember(registrationPrototype, 'sync'),
    periodicBackgroundSync: serviceWorker && hasMember(registrationPrototype, 'periodicSync'),
    share: has(navigatorRef?.share),
    clipboard: has(navigatorRef?.clipboard),
    vibration: has(navigatorRef?.vibrate),
    barcodeDetector: has(windowRef?.BarcodeDetector),
    standalone,
    online: navigatorRef?.onLine !== false,
    // On iOS the requirement holds until the app is actually launched from the
    // Home Screen, regardless of whether PushManager happens to be present.
    pushRequiresInstall: ios && !standalone,
    // WebKit on iOS and every browser tab in general: no guaranteed execution
    // once suspended. Never promise Android-style background GPS here.
    backgroundLocationUnavailable: ios || !serviceWorker,
  }
}

/** Reads the live browser environment. Safe to call during SSR/tests. */
export function readCapabilityEnvironment(): CapabilityEnvironment {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return {}
  const registrationPrototype = typeof (window as any).ServiceWorkerRegistration === 'function'
    ? (window as any).ServiceWorkerRegistration.prototype
    : undefined
  return {
    navigator: navigator as unknown as CapabilityEnvironment['navigator'],
    window: window as unknown as CapabilityEnvironment['window'],
    serviceWorkerRegistrationPrototype: registrationPrototype,
  }
}

export function detectBrowserCapabilities(): BrowserCapabilities {
  return resolveBrowserCapabilities(readCapabilityEnvironment())
}
